import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripApi } from '../../api/groupTripApi';
import {
  appendGroupTripDiscoveryItems,
  buildListPublicParams,
  emptyDiscoveryFilters,
  formatDiscoveryDateTime,
  GROUP_TRIP_DISCOVERY_SORTS,
  isDiscoveryDateRangeValid,
  isDiscoveryFilterActive,
  normalizeGroupTripDiscoveryPage,
} from './groupTripDiscoveryModel';
import { groupTripErrorMessage, occupancyCopy } from './groupTripModel';
import HostReputationStrip from './HostReputationStrip';
import './group-trip.css';

export default function PublicGroupTripsPage() {
  const { locale, t, tf } = useTravelTranslation();
  const filtersId = useId();

  const [pending, setPending] = useState(() => emptyDiscoveryFilters());
  const [appliedFilters, setAppliedFilters] = useState(() => emptyDiscoveryFilters());
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [dateValidationError, setDateValidationError] = useState('');

  const generation = useRef(0);
  const abortRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const load = useCallback(async ({ append = false, cursor = null, filters = appliedFilters } = {}) => {
    const requestGeneration = ++generation.current;
    if (!append && abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    if (!append) abortRef.current = controller;

    if (append) {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadMoreError('');
      setPhase('loading-more');
    } else {
      setError('');
      setLoadMoreError('');
      setPhase('loading');
    }

    try {
      const params = buildListPublicParams(filters, { cursor, limit: 20 });
      const payload = await groupTripApi.listPublic(params, { signal: controller.signal });
      if (requestGeneration !== generation.current) return;

      const page = normalizeGroupTripDiscoveryPage(payload, t);
      setItems((current) => (append
        ? appendGroupTripDiscoveryItems(current, page.items)
        : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (cause?.name === 'CanceledError' || cause?.name === 'AbortError' || cause?.code === 'ERR_CANCELED') {
        return;
      }
      if (requestGeneration !== generation.current) return;
      const message = groupTripErrorMessage(cause, t);
      if (append) {
        setLoadMoreError(message);
        setPhase('ready');
      } else {
        setError(message);
        setPhase('error');
      }
    } finally {
      if (append) loadingMoreRef.current = false;
    }
  }, [appliedFilters, t]);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setError('');
    setLoadMoreError('');
    load({ filters: appliedFilters });
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [appliedFilters, load]);

  function commitFilters(next) {
    setAppliedFilters(emptyDiscoveryFilters(next));
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    if (!isDiscoveryDateRangeValid(pending.startFrom, pending.startTo)) {
      setDateValidationError(t('travel.groupTrip.discovery.invalidDateRange'));
      return;
    }
    setDateValidationError('');
    commitFilters({
      q: pending.q,
      startFrom: pending.startFrom,
      startTo: pending.startTo,
      hasAvailableSlots: pending.hasAvailableSlots,
      sort: pending.sort,
    });
  }

  function handleSortChange(event) {
    const sort = event.target.value;
    setPending((current) => ({ ...current, sort }));
    commitFilters({
      ...appliedFilters,
      sort,
    });
  }

  function handleResetFilters() {
    const defaults = emptyDiscoveryFilters();
    setPending(defaults);
    setDateValidationError('');
    commitFilters(defaults);
  }

  function handleLoadMore() {
    if (!hasMore || phase === 'loading-more' || loadingMoreRef.current) return;
    load({ append: true, cursor: nextCursor, filters: appliedFilters });
  }

  const isInitialLoading = phase === 'loading' && items.length === 0;
  const isFilterRefreshing = phase === 'loading' && items.length > 0;
  const filtersActive = isDiscoveryFilterActive(appliedFilters);
  const showDefaultEmpty = phase === 'ready' && items.length === 0 && !filtersActive;
  const showFilterEmpty = phase === 'ready' && items.length === 0 && filtersActive;

  return (
    <main className="group-trips">
      <header className="group-trips__intro">
        <p>{t('travel.common.travel')}</p>
        <h1>{t('travel.groupTrip.publicListTitle')}</h1>
        <span>{t('travel.groupTrip.publicListHint')}</span>
      </header>

      <section className="group-trips__discovery" aria-labelledby={`${filtersId}-title`}>
        <header className="group-trips__discovery-header">
          <h2 id={`${filtersId}-title`} className="group-trips__discovery-title">
            {t('travel.groupTrip.discovery.filtersLabel')}
          </h2>
          <p className="group-trips__discovery-description">
            {t('travel.groupTrip.discovery.filtersDescription')}
          </p>
        </header>
        <form className="group-trips__discovery-form" onSubmit={handleSearchSubmit}>
          <div className="group-trips__discovery-row group-trips__discovery-row--fields">
            <label>
              {t('travel.groupTrip.discovery.keywordLabel')}
              <input
                type="search"
                name="q"
                value={pending.q}
                placeholder={t('travel.groupTrip.discovery.keywordPlaceholder')}
                onChange={(event) => setPending((current) => ({ ...current, q: event.target.value }))}
              />
            </label>
            <label>
              {t('travel.groupTrip.discovery.startFromLabel')}
              <input
                type="datetime-local"
                name="startFrom"
                value={pending.startFrom}
                aria-describedby={`${filtersId}-date-hint`}
                onChange={(event) => {
                  setDateValidationError('');
                  setPending((current) => ({ ...current, startFrom: event.target.value }));
                }}
              />
            </label>
            <label>
              {t('travel.groupTrip.discovery.startToLabel')}
              <input
                type="datetime-local"
                name="startTo"
                value={pending.startTo}
                aria-describedby={`${filtersId}-date-hint`}
                onChange={(event) => {
                  setDateValidationError('');
                  setPending((current) => ({ ...current, startTo: event.target.value }));
                }}
              />
            </label>
          </div>
          <p
            id={`${filtersId}-date-hint`}
            className="group-trips__field-hint group-trips__field-hint--discovery"
          >
            {t('travel.groupTrip.discovery.startFromHint')}
          </p>
          <div className="group-trips__discovery-row group-trips__discovery-row--controls">
            <label className="group-trips__checkbox">
              <input
                type="checkbox"
                name="hasAvailableSlots"
                checked={pending.hasAvailableSlots}
                onChange={(event) => setPending((current) => ({
                  ...current,
                  hasAvailableSlots: event.target.checked,
                }))}
              />
              <span>{t('travel.groupTrip.discovery.availableSlotsOnly')}</span>
            </label>
            <label className="group-trips__discovery-sort">
              {t('travel.groupTrip.discovery.sortLabel')}
              <select name="sort" value={pending.sort} onChange={handleSortChange}>
                {GROUP_TRIP_DISCOVERY_SORTS.map((sort) => (
                  <option key={sort} value={sort}>
                    {sort === 'START_AT_ASC'
                      ? t('travel.groupTrip.discovery.sortStartAtAsc')
                      : t('travel.groupTrip.discovery.sortLatest')}
                  </option>
                ))}
              </select>
            </label>
            <div className="group-trips__discovery-actions">
              <button type="submit">{t('travel.groupTrip.discovery.searchSubmit')}</button>
              <button type="button" onClick={handleResetFilters}>
                {t('travel.groupTrip.discovery.clearFilters')}
              </button>
            </div>
          </div>
        </form>
        {dateValidationError ? (
          <p className="group-trips__error" role="alert">{dateValidationError}</p>
        ) : null}
      </section>

      {isInitialLoading ? (
        <p aria-live="polite">{t('travel.common.loading')}</p>
      ) : null}

      {phase === 'error' && items.length === 0 ? (
        <section className="group-trips__state group-trips__state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => load({ filters: appliedFilters })}>
            {t('travel.common.retry')}
          </button>
        </section>
      ) : null}

      {showDefaultEmpty ? (
        <p>{t('travel.groupTrip.noPublicRecruitment')}</p>
      ) : null}

      {showFilterEmpty ? (
        <section className="group-trips__state">
          <p>{t('travel.groupTrip.discovery.noFilterResults')}</p>
          <p className="group-trips__hint">{t('travel.groupTrip.discovery.noFilterResultsHint')}</p>
          <button type="button" onClick={handleResetFilters}>
            {t('travel.groupTrip.discovery.clearFilters')}
          </button>
        </section>
      ) : null}

      {isFilterRefreshing ? (
        <p className="group-trips__hint" aria-live="polite">{t('travel.groupTrip.discovery.filterRefreshing')}</p>
      ) : null}

      <ul className="group-trips__list" aria-busy={isFilterRefreshing || phase === 'loading-more'}>
        {items.map((item) => (
          <li
            className="group-trips__card"
            key={item.groupTripId}
            data-occupancy={Number(item.availableSlots) < 1 ? 'full' : 'open'}
          >
            <article className="group-trips__card-body">
              <Link className="group-trips__card-main" to={`/public/group-trips/${item.groupTripId}`}>
                <h2>{item.recruitmentTitle}</h2>
                {item.summary ? <p className="group-trips__summary">{item.summary}</p> : null}
                {item.startAt ? (
                  <p className="group-trips__hint">
                    {tf('travel.groupTrip.discovery.departureAt', {
                      time: formatDiscoveryDateTime(item.startAt, locale),
                    })}
                  </p>
                ) : null}
                <p className="group-trips__hint" data-testid="public-group-trip-card-occupancy">
                  {occupancyCopy(item, t, tf)}
                </p>
              </Link>
              <HostReputationStrip
                host={item.host}
                averageRating={item.hostReputation?.averageRating}
                reviewCount={item.hostReputation?.reviewCount}
              />
            </article>
          </li>
        ))}
      </ul>

      {error && items.length > 0 ? (
        <p className="group-trips__error" role="alert">{error}</p>
      ) : null}

      {loadMoreError ? (
        <section className="group-trips__state group-trips__state--error" role="alert">
          <p>{loadMoreError}</p>
          <button type="button" onClick={handleLoadMore}>{t('travel.common.retry')}</button>
        </section>
      ) : null}

      {hasMore ? (
        <div className="group-trips__more">
          <button
            type="button"
            disabled={phase === 'loading-more'}
            onClick={handleLoadMore}
          >
            {phase === 'loading-more'
              ? t('travel.groupTrip.discovery.loadingMore')
              : t('travel.common.loadMore')}
          </button>
        </div>
      ) : null}
    </main>
  );
}
